import React from 'react';
import { CustomFunnelAuth } from 'korint-sdk/react';
import type {
  ConditionalVisibility,
  CustomFunnelConfig,
  Field,
  FieldShortcut,
  KorintModulesProductsConfig,
  FormSectionsWithShortcuts,
  InformationFieldParams,
  InformationSeparatorParams,
  FormSections,
} from 'korint-sdk/react';
import { tutassurHeader } from '../layout/header';
import { tutassurFooter } from '../layout/footer';
import { DocumentsUploadPolicyModule, InvoicesScheduleModule } from '@folio/modules';
import { ContactKey } from '@folio/utils/customerDisplayName';
import { centsToEuros, eurosToCents } from '@folio/utils/amount-utils';
import {
  ManualQuoteComponentDto,
  ManualQuotePercentageDto,
  PolicyDto,
  policyControllerConfirmPolicy,
  policyControllerGeneratePolicySignatureRequest,
  policyControllerPushPolicyEvent,
} from 'korint-sdk';
import {
  AlertTriangle,
  BadgeCheck,
  BriefcaseIcon,
  Building2,
  CalendarCheck,
  CalendarClock,
  CalendarDays,
  CalendarRange,
  Euro,
  FilePen,
  FileUp,
  Hash,
  Mail,
  MapPin,
  PartyPopper,
  Percent,
  Phone,
  Scale,
  ScaleIcon,
  Search,
  Shield,
  Store,
  User,
  Users,
  Wallet,
} from 'lucide-react';
import { createAnonymousGeneratedDocumentsStep } from '../../shared/anonymousGeneratedDocumentsStep';
import { z, ZodTypeAny } from 'zod';

const showWhenFilialeExists = (index: number): ConditionalVisibility[] => [
  {
    fieldName: `isFilialeBeingCreated${index}`,
    condition: 'equals',
    value: 'false',
  },
];

const showWhenFilialeBeingCreated = (index: number): ConditionalVisibility[] => [
  {
    fieldName: `isFilialeBeingCreated${index}`,
    condition: 'equals',
    value: 'true',
  },
];

const showWhenSCIExists = (index: number): ConditionalVisibility[] => [
  {
    fieldName: `isSCIBeingCreated${index}`,
    condition: 'equals',
    value: 'false',
  },
];

const showWhenSCIBeingCreated = (index: number): ConditionalVisibility[] => [
  {
    fieldName: `isSCIBeingCreated${index}`,
    condition: 'equals',
    value: 'true',
  },
];

const showWhenEtablissementExists = (index: number): ConditionalVisibility[] => [
  {
    fieldName: `isEtablissementBeingCreated${index}`,
    condition: 'equals',
    value: 'false',
  },
];

const showWhenEtablissementBeingCreated = (index: number): ConditionalVisibility[] => [
  {
    fieldName: `isEtablissementBeingCreated${index}`,
    condition: 'equals',
    value: 'true',
  },
];

// ============================================================================
// Form Sections
// ============================================================================
const companyCreationAdditionalFields: FieldShortcut[] = [
  {
    name: 'companyNafCode',
    type: 'text',
    icon: Hash,
    DEPRECATED_placeholder: '6201Z',
    isCustomField: true,
    showWhen: [{ fieldName: 'isCompanyBeingCreated', condition: 'equals', value: true }],
  },
];

const customerFunnelSections = [
  {
    title: 'Signataire',
    fields: [
      { name: 'contactCivility', icons: { MONSIEUR: User, MADAME: User, MADEMOISELLE: User } },
      { name: 'contactFirstName', colSpan: 6 as const, icon: User },
      { name: 'contactLastName', colSpan: 6 as const, icon: User },
      { name: 'contactEmail', colSpan: 6 as const, icon: Mail },
      { name: 'contactPhoneNumber', colSpan: 6 as const, icon: Phone },
      { name: 'contactBirthDate', colSpan: 6 as const, icon: CalendarDays },
      {
        name: 'contactBirthCity',
        type: 'city',
        addressGroup: 'contactBirth',
        colSpan: 6 as const,
        icon: MapPin,
      },
      { name: 'contactCompanyRole', icon: BriefcaseIcon },
    ],
  },
] as const satisfies FormSectionsWithShortcuts;

const financialCompanySections: FormSectionsWithShortcuts = [
  {
    title: "Informations complémentaires sur l'entreprise",
    fields: [
      { name: 'companyNumberOfEmployees', colSpan: 6 as const, icon: Users },
      { name: 'companyNumberOfVTM', colSpan: 6 as const, icon: Users },
      { name: 'lastExerciseOrForecastRevenue', colSpan: 6 as const, icon: Euro },
      { name: 'yearOfRevenue', colSpan: 6 as const, icon: CalendarDays },
    ],
  },
];

// @debt The structure of the establishments questions is quite similar to the one of the subsidiaries, we could probably reuse some components to avoid duplication
const questionsSCI: FormSectionsWithShortcuts = [
  {
    title: 'Déclaration de SCI',
    fields: [
      { name: 'specificQuestionRCP1', display: 'toggle' as const, icon: Scale },
      {
        name: 'nbSCIs',
        icon: Hash,
        type: 'number' as const,
        integer: true,
        min: 0,
        max: 5,
        display: 'cards' as const,
        displayMin: 0,
        displayMax: 5,
        labels: { 0: 'Aucune' },
        showWhen: [
          {
            fieldName: 'specificQuestionRCP1',
            condition: 'equals',
            value: 'true',
          },
        ],
      },
    ],
  },
  ...(Array.from({ length: 5 }, (_, index) => ({
    title: `SCI ${index + 1}`,
    display: 'column' as const,
    showWhen: [
      {
        fieldName: 'nbSCIs',
        condition: 'greaterThanOrEquals' as const,
        value: index + 1,
      },
    ],
    fields: [
      {
        name: `isSCIBeingCreated${index + 1}`,
        display: 'toggle' as const,
        required: false,
      },
      {
        name: `sciSiret${index + 1}`,
        type: 'company',
        displayCompanyInformation: true,
        icon: Search,
        // biome-ignore lint/suspicious/noExplicitAny: dynamic field
        apiDataAccessor: (apiData: any) => {
          const customFields = apiData.customFields as Record<string, string> | undefined;
          const siret = customFields?.[`sciSiret${index + 1}`];
          return siret ? { siret } : undefined;
        },
        formDataAccessor: `sciSiret${index + 1}.siret`,
        required: index === 0,
        isCustomField: true,
        clearable: true,
        showWhen: showWhenSCIExists(index + 1),
      },
      {
        name: `sciName${index + 1}`,
        type: 'text',
        icon: Building2,
        DEPRECATED_placeholder: 'SCI Dupont',
        isCustomField: true,
        showWhen: showWhenSCIBeingCreated(index + 1),
        required: true,
      },
      {
        name: `sciHeadquarterAddress${index + 1}`,
        type: 'address',
        icon: MapPin,
        DEPRECATED_placeholder: '12 rue de la Paix',
        isCustomField: true,
        showWhen: showWhenSCIBeingCreated(index + 1),
        required: true,
      },
      {
        name: `sciHeadquarterPostcode${index + 1}`,
        type: 'postcode',
        colSpan: 4 as const,
        icon: Hash,
        DEPRECATED_placeholder: '75001',
        isCustomField: true,
        showWhen: showWhenSCIBeingCreated(index + 1),
        required: true,
      },
      {
        name: `sciHeadquarterCity${index + 1}`,
        type: 'city',
        colSpan: 8 as const,
        icon: Building2,
        DEPRECATED_placeholder: 'Paris',
        isCustomField: true,
        showWhen: showWhenSCIBeingCreated(index + 1),
        required: true,
      },
      // When SCI already exists (not being created), derive info from the SIRET picker
      {
        name: `sciNameFromSiret${index + 1}`,
        type: 'computed',
        required: false,
        hidden: true,
        isCustomField: true,
        payloadKeyName: `sciName${index + 1}`,
        dependsOn: [`sciSiret${index + 1}`],
        formDataAccessor: (formData: Record<string, unknown>) =>
          (formData[`sciSiret${index + 1}`] as { name?: string } | undefined)?.name,
      },
      {
        name: `sciHeadquarterAddressFromSiret${index + 1}`,
        type: 'computed',
        required: false,
        hidden: true,
        isCustomField: true,
        payloadKeyName: `sciHeadquarterAddress${index + 1}`,
        dependsOn: [`sciSiret${index + 1}`],
        formDataAccessor: (formData: Record<string, unknown>) =>
          (formData[`sciSiret${index + 1}`] as { location?: { address?: string } } | undefined)
            ?.location?.address,
      },
      {
        name: `sciHeadquarterPostcodeFromSiret${index + 1}`,
        type: 'computed',
        required: false,
        hidden: true,
        isCustomField: true,
        payloadKeyName: `sciHeadquarterPostcode${index + 1}`,
        dependsOn: [`sciSiret${index + 1}`],
        formDataAccessor: (formData: Record<string, unknown>) =>
          (formData[`sciSiret${index + 1}`] as { location?: { postCode?: string } } | undefined)
            ?.location?.postCode,
      },
      {
        name: `sciHeadquarterCityFromSiret${index + 1}`,
        type: 'computed',
        required: false,
        hidden: true,
        isCustomField: true,
        payloadKeyName: `sciHeadquarterCity${index + 1}`,
        dependsOn: [`sciSiret${index + 1}`],
        formDataAccessor: (formData: Record<string, unknown>) =>
          (formData[`sciSiret${index + 1}`] as { location?: { city?: string } } | undefined)
            ?.location?.city,
      },
      {
        name: `sciNafCode${index + 1}`,
        type: 'text',
        icon: Hash,
        DEPRECATED_placeholder: '6201Z',
        isCustomField: true,
        showWhen: showWhenSCIBeingCreated(index + 1),
        required: false,
      },
      {
        name: `sciNafCodeFromSiret${index + 1}`,
        type: 'computed',
        required: false,
        hidden: true,
        isCustomField: true,
        payloadKeyName: `sciNafCode${index + 1}`,
        dependsOn: [`sciSiret${index + 1}`],
        formDataAccessor: (formData: Record<string, unknown>) =>
          (formData[`sciSiret${index + 1}`] as { nafCode?: string } | undefined)?.nafCode,
      },
    ],
  })) as FormSectionsWithShortcuts),
];

// @debt The structure of the establishments questions is quite similar to the one of the subsidiaries, we could probably reuse some components to avoid duplication
const questionsFiliales: FormSectionsWithShortcuts = [
  {
    title: 'Déclarations de filiales',
    fields: [
      { name: 'specificQuestionRCP2', display: 'toggle' as const, icon: Users },
      {
        name: 'nbFiliales',
        icon: Hash,
        type: 'number' as const,
        integer: true,
        min: 0,
        max: 5,
        display: 'cards' as const,
        displayMin: 0,
        displayMax: 5,
        labels: { 0: 'Aucune' },
        showWhen: [
          {
            fieldName: 'specificQuestionRCP2',
            condition: 'equals',
            value: 'true',
          },
        ],
      },
    ],
  },
  ...(Array.from({ length: 5 }, (_, index) => ({
    title: `Filiale ${index + 1}`,
    display: 'column' as const,
    showWhen: [
      {
        fieldName: 'nbFiliales',
        condition: 'greaterThanOrEquals' as const,
        value: index + 1,
      },
    ],
    fields: [
      {
        name: `isFilialeBeingCreated${index + 1}`,
        display: 'toggle' as const,
        required: false,
      },
      {
        name: `filialeSiret${index + 1}`,
        type: 'company',
        displayCompanyInformation: true,
        icon: Search,
        // biome-ignore lint/suspicious/noExplicitAny: dynamic field
        apiDataAccessor: (apiData: any) => {
          const customFields = apiData.customFields as Record<string, string> | undefined;
          const siret = customFields?.[`filialeSiret${index + 1}`];
          return siret ? { siret } : undefined;
        },
        formDataAccessor: `filialeSiret${index + 1}.siret`,
        required: index === 0,
        isCustomField: true,
        clearable: true,
        showWhen: showWhenFilialeExists(index + 1),
      },
      {
        name: `filialeName${index + 1}`,
        type: 'text',
        icon: Building2,
        DEPRECATED_placeholder: 'Filiale Dupont',
        isCustomField: true,
        showWhen: showWhenFilialeBeingCreated(index + 1),
        required: true,
      },
      {
        name: `filialeHeadquarterAddress${index + 1}`,
        type: 'address',
        icon: MapPin,
        DEPRECATED_placeholder: '12 rue de la Paix',
        isCustomField: true,
        showWhen: showWhenFilialeBeingCreated(index + 1),
        required: true,
      },
      {
        name: `filialeHeadquarterPostcode${index + 1}`,
        type: 'postcode',
        colSpan: 4 as const,
        icon: Hash,
        DEPRECATED_placeholder: '75001',
        isCustomField: true,
        showWhen: showWhenFilialeBeingCreated(index + 1),
        required: true,
      },
      {
        name: `filialeHeadquarterCity${index + 1}`,
        type: 'city',
        colSpan: 8 as const,
        icon: Building2,
        DEPRECATED_placeholder: 'Paris',
        isCustomField: true,
        showWhen: showWhenFilialeBeingCreated(index + 1),
        required: true,
      },
      // When filiale already exists (not being created), derive info from the SIRET picker
      {
        name: `filialeNameFromSiret${index + 1}`,
        type: 'computed',
        required: false,
        hidden: true,
        isCustomField: true,
        payloadKeyName: `filialeName${index + 1}`,
        dependsOn: [`filialeSiret${index + 1}`],
        formDataAccessor: (formData: Record<string, unknown>) =>
          (formData[`filialeSiret${index + 1}`] as { name?: string } | undefined)?.name,
      },
      {
        name: `filialeHeadquarterAddressFromSiret${index + 1}`,
        type: 'computed',
        required: false,
        hidden: true,
        isCustomField: true,
        payloadKeyName: `filialeHeadquarterAddress${index + 1}`,
        dependsOn: [`filialeSiret${index + 1}`],
        formDataAccessor: (formData: Record<string, unknown>) =>
          (formData[`filialeSiret${index + 1}`] as { location?: { address?: string } } | undefined)
            ?.location?.address,
      },
      {
        name: `filialeHeadquarterPostcodeFromSiret${index + 1}`,
        type: 'computed',
        required: false,
        hidden: true,
        isCustomField: true,
        payloadKeyName: `filialeHeadquarterPostcode${index + 1}`,
        dependsOn: [`filialeSiret${index + 1}`],
        formDataAccessor: (formData: Record<string, unknown>) =>
          (formData[`filialeSiret${index + 1}`] as { location?: { postCode?: string } } | undefined)
            ?.location?.postCode,
      },
      {
        name: `filialeHeadquarterCityFromSiret${index + 1}`,
        type: 'computed',
        required: false,
        hidden: true,
        isCustomField: true,
        payloadKeyName: `filialeHeadquarterCity${index + 1}`,
        dependsOn: [`filialeSiret${index + 1}`],
        formDataAccessor: (formData: Record<string, unknown>) =>
          (formData[`filialeSiret${index + 1}`] as { location?: { city?: string } } | undefined)
            ?.location?.city,
      },
      {
        name: `filialeNafCode${index + 1}`,
        type: 'text',
        icon: Hash,
        DEPRECATED_placeholder: '6201Z',
        isCustomField: true,
        showWhen: showWhenFilialeBeingCreated(index + 1),
        required: false,
      },
      {
        name: `filialeNafCodeFromSiret${index + 1}`,
        type: 'computed',
        required: false,
        hidden: true,
        isCustomField: true,
        payloadKeyName: `filialeNafCode${index + 1}`,
        dependsOn: [`filialeSiret${index + 1}`],
        formDataAccessor: (formData: Record<string, unknown>) =>
          (formData[`filialeSiret${index + 1}`] as { nafCode?: string } | undefined)?.nafCode,
      },
    ],
  })) as FormSectionsWithShortcuts),
];

// @debt The structure of the establishments questions is quite similar to the one of the subsidiaries, we could probably reuse some components to avoid duplication
const questionsEtablissements: FormSectionsWithShortcuts = [
  {
    title: 'Déclaration des établissements secondaires',
    fields: [
      { name: 'specificQuestionRCP3', display: 'toggle' as const, icon: Store },
      {
        name: 'nbEtablissements',
        icon: Hash,
        type: 'number' as const,
        integer: true,
        min: 0,
        max: 5,
        display: 'cards' as const,
        displayMin: 0,
        displayMax: 5,
        labels: { 0: 'Aucune' },
        showWhen: [
          {
            fieldName: 'specificQuestionRCP3',
            condition: 'equals',
            value: 'true',
          },
        ],
      },
    ],
  },
  ...(Array.from({ length: 5 }, (_, index) => ({
    title: `Établissement ${index + 1}`,
    display: 'column' as const,
    showWhen: [
      {
        fieldName: 'nbEtablissements',
        condition: 'greaterThanOrEquals' as const,
        value: index + 1,
      },
    ],
    fields: [
      {
        name: `isEtablissementBeingCreated${index + 1}`,
        display: 'toggle' as const,
        required: false,
      },
      {
        name: `etablissementSiret${index + 1}`,
        type: 'company',
        displayCompanyInformation: true,
        icon: Search,
        // biome-ignore lint/suspicious/noExplicitAny: dynamic field
        apiDataAccessor: (apiData: any) => {
          const customFields = apiData.customFields as Record<string, string> | undefined;
          const siret = customFields?.[`etablissementSiret${index + 1}`];
          return siret ? { siret } : undefined;
        },
        formDataAccessor: `etablissementSiret${index + 1}.siret`,
        required: index === 0,
        isCustomField: true,
        clearable: true,
        showWhen: showWhenEtablissementExists(index + 1),
      },
      {
        name: `etablissementName${index + 1}`,
        type: 'text',
        icon: Building2,
        DEPRECATED_placeholder: 'Établissement Paris',
        isCustomField: true,
        showWhen: showWhenEtablissementBeingCreated(index + 1),
        required: true,
      },
      {
        name: `etablissementHeadquarterAddress${index + 1}`,
        type: 'address',
        icon: MapPin,
        DEPRECATED_placeholder: '12 rue de la Paix',
        isCustomField: true,
        showWhen: showWhenEtablissementBeingCreated(index + 1),
        required: true,
      },
      {
        name: `etablissementHeadquarterPostcode${index + 1}`,
        type: 'postcode',
        colSpan: 4 as const,
        icon: Hash,
        DEPRECATED_placeholder: '75001',
        isCustomField: true,
        showWhen: showWhenEtablissementBeingCreated(index + 1),
        required: true,
      },
      {
        name: `etablissementHeadquarterCity${index + 1}`,
        type: 'city',
        colSpan: 8 as const,
        icon: Building2,
        DEPRECATED_placeholder: 'Paris',
        isCustomField: true,
        showWhen: showWhenEtablissementBeingCreated(index + 1),
        required: true,
      },
      // When établissement already exists (not being created), derive info from the SIRET picker
      {
        name: `etablissementNameFromSiret${index + 1}`,
        type: 'computed',
        required: false,
        hidden: true,
        isCustomField: true,
        payloadKeyName: `etablissementName${index + 1}`,
        dependsOn: [`etablissementSiret${index + 1}`],
        formDataAccessor: (formData: Record<string, unknown>) =>
          (formData[`etablissementSiret${index + 1}`] as { name?: string } | undefined)?.name,
      },
      {
        name: `etablissementHeadquarterAddressFromSiret${index + 1}`,
        type: 'computed',
        required: false,
        hidden: true,
        isCustomField: true,
        payloadKeyName: `etablissementHeadquarterAddress${index + 1}`,
        dependsOn: [`etablissementSiret${index + 1}`],
        formDataAccessor: (formData: Record<string, unknown>) =>
          (
            formData[`etablissementSiret${index + 1}`] as
              | { location?: { address?: string } }
              | undefined
          )?.location?.address,
      },
      {
        name: `etablissementHeadquarterPostcodeFromSiret${index + 1}`,
        type: 'computed',
        required: false,
        hidden: true,
        isCustomField: true,
        payloadKeyName: `etablissementHeadquarterPostcode${index + 1}`,
        dependsOn: [`etablissementSiret${index + 1}`],
        formDataAccessor: (formData: Record<string, unknown>) =>
          (
            formData[`etablissementSiret${index + 1}`] as
              | { location?: { postCode?: string } }
              | undefined
          )?.location?.postCode,
      },
      {
        name: `etablissementHeadquarterCityFromSiret${index + 1}`,
        type: 'computed',
        required: false,
        hidden: true,
        isCustomField: true,
        payloadKeyName: `etablissementHeadquarterCity${index + 1}`,
        dependsOn: [`etablissementSiret${index + 1}`],
        formDataAccessor: (formData: Record<string, unknown>) =>
          (
            formData[`etablissementSiret${index + 1}`] as
              | { location?: { city?: string } }
              | undefined
          )?.location?.city,
      },
      {
        name: `etablissementNafCode${index + 1}`,
        type: 'text',
        icon: Hash,
        DEPRECATED_placeholder: '6201Z',
        isCustomField: true,
        showWhen: showWhenEtablissementBeingCreated(index + 1),
        required: false,
      },
      {
        name: `etablissementNafCodeFromSiret${index + 1}`,
        type: 'computed',
        required: false,
        hidden: true,
        isCustomField: true,
        payloadKeyName: `etablissementNafCode${index + 1}`,
        dependsOn: [`etablissementSiret${index + 1}`],
        formDataAccessor: (formData: Record<string, unknown>) =>
          (formData[`etablissementSiret${index + 1}`] as { nafCode?: string } | undefined)?.nafCode,
      },
    ],
  })) as FormSectionsWithShortcuts),
];

const KILLING_MESSAGE = 'Il est impossible de souscrire une police dans ce cas';

const killingQuestionBlockingValidation = {
  display: 'toggle' as const,
  blockingValue: 'true',
  blockingErrorMessage: KILLING_MESSAGE,
  additionalValidationRules: (schema: ZodTypeAny) =>
    schema.and(
      z.literal('false', {
        errorMap: () => ({ message: KILLING_MESSAGE }),
      }),
    ),
};

const killingQuestionField = (name: string, icon: React.ComponentType<{ className?: string }>) => ({
  name,
  icon,
  ...killingQuestionBlockingValidation,
});

const priorHistorySections: FormSectionsWithShortcuts = [
  {
    title: 'Historique des Antécédents',
    fields: [
      {
        name: 'kqPriorHistoryNbPJ',
        icon: AlertTriangle,
        type: 'number' as const,
        integer: true,
        min: 0,
        display: 'cards' as const,
        displayMin: 0,
        displayMax: 6,
        labels: { 0: 'Aucune' },
      },
      { name: 'kqPriorHistoryQ0', display: 'toggle' as const, icon: AlertTriangle },
      killingQuestionField('kqPriorHistoryQ1', AlertTriangle),
      killingQuestionField('kqPriorHistoryQ2', AlertTriangle),
    ],
  },
];

// ============================================================================
// Manual Quote Sections (Wholesale Re-pricing)
// ============================================================================
const premiumFormDataAccessor = (data: Record<string, unknown>) => {
  const amountWithFeesPreTaxes =
    eurosToCents(data.premiumAmountWithIncludedWholesaleFee as number | undefined) ?? 0;

  const includedWholesaleFees = eurosToCents(data.includedWholesaleFees as number | undefined) ?? 0;

  const totalTaxes = eurosToCents(data.premiumTotalTaxes as number | undefined) ?? 0;

  return {
    manualPremiumComponent: {
      id: 'PROTECTION_JURIDIQUE_PRO_PREMIUM',
      chargeType: 'RECURRING',
      premium: {
        amountPreFeesAndTaxes: amountWithFeesPreTaxes - includedWholesaleFees,
        totalFees: includedWholesaleFees,
        totalTaxes,
      },
      type: 'PERIL',
    } satisfies ManualQuoteComponentDto,
  };
};

const manualQuoteSections = [
  {
    title: 'Prime',
    fields: [
      {
        name: 'premiumAmountWithIncludedWholesaleFee',
        required: true,
        type: 'number',
        colSpan: 6 as const,
        icon: Euro,
        DEPRECATED_placeholder: '1 200',
        apiDataAccessor: (data: PolicyDto) => {
          const component = data?.quote?.components?.find(
            component => component.id === 'PROTECTION_JURIDIQUE_PRO_PREMIUM',
          );

          if (!component) return;

          return centsToEuros(component.premium.amountWithFeesPreTaxes);
        },
        formDataAccessor: premiumFormDataAccessor,
      },
      {
        name: 'includedWholesaleFees',
        required: true,
        type: 'number',
        colSpan: 6 as const,
        icon: Euro,
        DEPRECATED_placeholder: '100',
        apiDataAccessor: (data: PolicyDto) => {
          const component = data?.quote?.components?.find(
            component => component.id === 'PROTECTION_JURIDIQUE_PRO_PREMIUM',
          );

          if (!component) return;

          return centsToEuros(component.premium.totalFees);
        },
        formDataAccessor: premiumFormDataAccessor,
      },
      {
        name: 'premiumTotalTaxes',
        required: true,
        type: 'number',
        icon: Euro,
        DEPRECATED_placeholder: '50',
        apiDataAccessor: (data: PolicyDto) => {
          const component = data?.quote?.components?.find(
            component => component.id === 'PROTECTION_JURIDIQUE_PRO_PREMIUM',
          );

          if (!component) return;

          return centsToEuros(component.premium.totalTaxes);
        },
        formDataAccessor: premiumFormDataAccessor,
      },
    ] as const satisfies Array<Field>,
  },
] as const satisfies FormSectionsWithShortcuts;

// ============================================================================
// Additional Quote Components (Fees)
// ============================================================================
const additionalQuoteComponentsSections = [
  {
    title: 'Frais grossistes',
    forTypes: ['WHOLESALE'],
    fields: [
      {
        name: 'wholesaleAdditionalCommissionPercentage',
        required: false,
        type: 'number',
        integer: true,
        max: 100,
        colSpan: 6 as const,
        icon: Percent,
        DEPRECATED_placeholder: '10',
        apiDataAccessor: (data: PolicyDto) => {
          const wholesaleAdditionalCommission = data?.quote?.components?.find(
            c => c.id === 'WHOLESALE_ADDITIONAL_COMMISSION',
          );
          const premium = data?.quote?.components?.find(
            c => c.id === 'PROTECTION_JURIDIQUE_PRO_PREMIUM',
          );
          if (!wholesaleAdditionalCommission || !premium?.premium.amountPreFeesAndTaxes) return;
          return Math.round(
            (wholesaleAdditionalCommission.premium.amountWithFeesAndTaxes /
              premium.premium.amountPreFeesAndTaxes) *
              100,
          );
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const percentage = data.wholesaleAdditionalCommissionPercentage as number | undefined;
          if (!percentage) return {};
          return {
            wholesaleAdditionalCommissionComponent: {
              componentIds: ['PROTECTION_JURIDIQUE_PRO_PREMIUM'],
              id: 'WHOLESALE_ADDITIONAL_COMMISSION',
              type: 'FEE',
              chargeType: 'RECURRING',
              amountPropertiesFrom: ['amountPreFeesAndTaxes'],
              percentage,
            } satisfies ManualQuotePercentageDto,
          };
        },
      },
      {
        name: 'wholesaleSubscriptionFee',
        required: false,
        type: 'number',
        colSpan: 6 as const,
        icon: Euro,
        DEPRECATED_placeholder: '150',
        apiDataAccessor: (data: PolicyDto) => {
          const wholesaleSubscriptionFee = data?.quote?.components?.find(
            c => c.id === 'WHOLESALE_SUBSCRIPTION_FEE',
          );
          if (!wholesaleSubscriptionFee) return;
          return centsToEuros(wholesaleSubscriptionFee?.premium?.amountWithFeesAndTaxes);
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const totalFees = data.wholesaleSubscriptionFee as number | undefined;
          if (!totalFees) return {};
          return {
            wholesaleSubscriptionFeeComponent: {
              id: 'WHOLESALE_SUBSCRIPTION_FEE',
              type: 'FEE',
              chargeType: 'ONE_TIME',
              premium: {
                amountPreFeesAndTaxes: 0,
                totalFees: eurosToCents(totalFees) as number,
                totalTaxes: 0,
              },
            } satisfies ManualQuoteComponentDto,
          };
        },
      },
    ],
  },
  {
    title: 'Frais distributeurs',
    forTypes: ['WHOLESALE', 'RETAIL'],
    fields: [
      {
        name: 'intermediaryCommission',
        required: false,
        type: 'number',
        integer: true,
        max: 100,
        colSpan: 6 as const,
        icon: Percent,
        DEPRECATED_placeholder: '10',
        apiDataAccessor: (data: PolicyDto) => {
          const intermediaryCommission = data?.quote?.components?.find(
            c => c.id === 'RETAIL_COMMISSION',
          );
          const premium = data?.quote?.components?.find(
            c => c.id === 'PROTECTION_JURIDIQUE_PRO_PREMIUM',
          );
          if (!intermediaryCommission || !premium) return;
          return Math.round(
            (intermediaryCommission.premium.amountWithFeesAndTaxes /
              premium.premium.amountPreFeesAndTaxes) *
              100,
          );
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const percentage = data.intermediaryCommission as number | undefined;
          if (!percentage) return {};
          return {
            intermediaryCommissionPercentage: {
              componentIds: ['PROTECTION_JURIDIQUE_PRO_PREMIUM'],
              id: 'RETAIL_COMMISSION',
              type: 'FEE',
              chargeType: 'RECURRING',
              amountPropertiesFrom: ['amountPreFeesAndTaxes'],
              percentage: (data.intermediaryCommission ?? 0) as number,
            } satisfies ManualQuotePercentageDto,
          };
        },
      },
      {
        name: 'intermediarySubscriptionFee',
        required: false,
        type: 'number',
        colSpan: 6 as const,
        icon: Euro,
        DEPRECATED_placeholder: '100',
        apiDataAccessor: (data: PolicyDto) => {
          const retailSubscriptionFee = data?.quote?.components?.find(
            c => c.id === 'RETAIL_SUBSCRIPTION_FEE',
          );
          if (!retailSubscriptionFee) return;
          return centsToEuros(retailSubscriptionFee?.premium?.amountWithFeesAndTaxes);
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const totalFees = data.intermediarySubscriptionFee as number | undefined;
          if (!totalFees) return {};
          return {
            intermediarySubscriptionFeeComponent: {
              id: 'RETAIL_SUBSCRIPTION_FEE',
              type: 'FEE',
              chargeType: 'ONE_TIME',
              premium: {
                amountPreFeesAndTaxes: 0,
                totalFees: eurosToCents(totalFees) as number,
                totalTaxes: 0,
              },
            } satisfies ManualQuoteComponentDto,
          };
        },
      },
      {
        name: 'intermediaryManagementFee',
        required: false,
        type: 'number',
        icon: Euro,
        DEPRECATED_placeholder: '200',
        apiDataAccessor: (data: PolicyDto) => {
          const intermediaryManagementFee = data?.quote?.components?.find(
            c => c.id === 'RETAIL_MANAGEMENT_FEE',
          );
          if (!intermediaryManagementFee) return;
          return centsToEuros(intermediaryManagementFee?.premium?.amountWithFeesAndTaxes) ?? 0;
        },
        formDataAccessor: (data: Record<string, unknown>) => {
          const totalFees = data.intermediaryManagementFee as number | undefined;
          if (!totalFees) return {};
          return {
            intermediaryManagementFeeComponent: {
              id: 'RETAIL_MANAGEMENT_FEE',
              type: 'FEE',
              chargeType: 'RECURRING',
              premium: {
                amountPreFeesAndTaxes: 0,
                totalFees: eurosToCents(totalFees) ?? 0,
                totalTaxes: 0,
              },
            } satisfies ManualQuoteComponentDto,
          };
        },
      },
    ],
  },
] as const satisfies Array<FormSections[number] & { forTypes?: string[] }>;

// ============================================================================
// Anonymous Funnel Steps
// ============================================================================
const anonymousGeneratedDocumentsStep = createAnonymousGeneratedDocumentsStep({
  categories: ['QUOTE', 'PJ_PRO_TERMS_AND_CONDITIONS', 'IPID_PJ_PRO', 'BAREME'],
  requiredDocuments: [
    <span>Les informations relatives à votre activité professionnelle</span>,
    <span>Votre KBIS ou extrait d'immatriculation</span>,
    <span>Votre pièce d'identité</span>,
  ],
});

const documentsStep = {
  path: 'documents',
  type: 'module' as const,
  module: (
    <DocumentsUploadPolicyModule
      displayContext="policyholder"
      documentsLists={[{ category: 'signatory-id' }, { category: 'kbis' }, { category: 'iban' }]}
    />
  ),
};

// ============================================================================
// Anonymous Funnel Base Definitions
// ============================================================================

type AnonymousFunnelBase = Omit<
  CustomFunnelConfig,
  'name' | 'basePath' | 'afterEndPath' | 'layout' | 'customLayout'
>;

const tutassurCustomLayout = {
  header: tutassurHeader,
  footer: tutassurFooter,
};

const BILLING_FREQUENCY_ICONS = {
  MONTHLY: CalendarDays,
  QUARTERLY: CalendarRange,
  HALF_YEARLY: CalendarClock,
  YEARLY: CalendarCheck,
};

const anonymousDiscoveryBase: AnonymousFunnelBase = {
  auth: CustomFunnelAuth.ANONYMOUS,
  steps: [
    {
      path: 'asset',
      type: 'emptyAssetFunnel',
      hidden: true,
    },
    {
      path: 'company',
      type: 'companyFunnel',
      icon: Building2,
      additionalFields: companyCreationAdditionalFields,
      additionalSections: financialCompanySections,
    },
    {
      path: 'prior-history',
      type: 'policyFunnel',
      icon: AlertTriangle,
      sections: priorHistorySections,
    },
    {
      path: 'questions-filiales',
      type: 'policyFunnel',
      icon: Users,
      sections: questionsFiliales,
    },
    {
      path: 'questions-etablissements',
      type: 'policyFunnel',
      icon: Store,
      sections: questionsEtablissements,
    },
    {
      path: 'quote',
      type: 'quoteFunnel',
      icon: Shield,
      assetSections: [
        {
          title: 'Date de début de contrat',
          fields: [{ name: 'startedAt', min: 'startOfDay', icon: CalendarDays }],
        },
      ],
    },
    {
      path: 'questions-sci',
      type: 'policyFunnel',
      icon: Scale,
      sections: questionsSCI,
    },
    {
      path: 'customer',
      type: 'customerFunnel',
      icon: User,
      sections: customerFunnelSections,
      triggerEvent: 'TUTASSUR_ANONYMOUS_PRICING_COMPLETED',
      maxCustomers: 1,
      minCustomers: 1,
    },
    anonymousGeneratedDocumentsStep,
  ],
};

const anonymousSubscriptionBase: AnonymousFunnelBase = {
  auth: CustomFunnelAuth.ANONYMOUS,
  steps: [
    { ...documentsStep, icon: FileUp },
    {
      path: 'billing',
      type: 'billingSelectionFunnel',
      icon: CalendarClock,
      frequencyIcons: BILLING_FREQUENCY_ICONS,
    },
    {
      path: 'billing-assignment',
      type: 'billingAssignmentFunnel',
      icon: CalendarClock,
    },
    {
      path: 'payment-methods',
      type: 'paymentMethodFunnel',
      icon: Wallet,
    },
    {
      path: 'invoicing-schedule',
      type: 'module',
      icon: CalendarDays,
      module: <InvoicesScheduleModule />,
    },
    {
      path: 'end',
      type: 'funnelEnd' as const,
      id: 'anonymous-subscription-end',
      icon: PartyPopper,
      callback: {
        callToActionCallback: async ({
          policyId,
          branchId,
        }: {
          policyId: string;
          branchId: string;
        }) => {
          await policyControllerPushPolicyEvent(
            policyId,
            { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
            { branchId },
          );
          await policyControllerConfirmPolicy(policyId, { branchId });
          for (let attempt = 0; attempt < 100; attempt++) {
            try {
              await policyControllerGeneratePolicySignatureRequest(
                policyId,
                { deliveryMode: 'EMAIL', isOtp: false, phoneNumber: '' },
                { branchId },
              );
              break;
            } catch (error: unknown) {
              if ((error as any)?.errorResponse?.status === 425 && attempt < 99) {
                await new Promise(resolve => setTimeout(resolve, 1000));
                continue;
              }
              throw error;
            }
          }
        },
      },
      redirectTo: ({ policyId, branchId }) =>
        `/auth?redirect=${encodeURIComponent(`/policyholder/${policyId}/${branchId}`)}`,
      additionalComponents: {
        isQuoteTierModuleVisible: false,
        finishWithoutSendingButton: {
          enabled: false,
        },
      },
    },
  ],
};

// ============================================================================
// Product Configuration
// ============================================================================
export const tutassurProtectionJuridiqueProConfig = {
  display: {
    category: 'protection',
    icon: ScaleIcon,
    badges: ['online'],
  },
  productCustomPath: '/protection-juridique-pro',
  customerDefaultRole: 'POLICYHOLDER',

  // ============================================================================
  // Customer Information Display
  // ============================================================================
  customerInformations: {
    fieldsConfig: [
      // Avenant contractuel — identité de l'entreprise
      { key: 'companyName', contactKey: ContactKey.COMPANY_NAME },
      'companyHeadquarterAddress',
      'companyHeadquarterPostcode',
      'companyHeadquarterCity',
      'companyHeadquarterCountry',
      'companyLegalStatus1',
      'companySiret',
      'companyNafCode',
      // Avenant tarifant
      { type: 'separator' as const, id: 'informationsFinancieres' },
      'companyNumberOfEmployees',
      'companyNumberOfVTM',
      'lastExerciseOrForecastRevenue',
      // Avenant non contractuel — signataire
      { type: 'separator' as const, id: 'signataire' },
      'contactCivility',
      { key: 'contactFirstName', contactKey: ContactKey.FIRST_NAME },
      { key: 'contactLastName', contactKey: ContactKey.LAST_NAME },
      'contactEmail',
      'contactPhoneNumber',
      'contactCompanyRole',
    ],
  },

  // ============================================================================
  // Policy Information Display
  // ============================================================================
  policyInformationCard: {
    title: 'Informations contractuelles',
    fieldsConfig: [
      // SCI
      {
        key: 'specificQuestionRCP1',
        type: 'boolean',
      },
      ...Array.from({ length: 5 }, (_, i) => i + 1).flatMap(
        (index): Array<InformationFieldParams | InformationSeparatorParams> => [
          ...(index > 1
            ? [
                {
                  type: 'separator' as const,
                  id: `sci${index}`,
                  hideIfFieldEmpty: `sciSiret${index}`,
                },
              ]
            : []),
          { key: `sciSiret${index}`, type: 'siret', hideIfEmpty: true },
          { key: `sciName${index}`, type: 'text', hideIfEmpty: true },
          { key: `sciHeadquarterAddress${index}`, type: 'text', hideIfEmpty: true },
          {
            key: `sciHeadquarterPostcode${index}`,
            type: 'postcode',
            countries: ['FR'],
            hideIfEmpty: true,
          },
          { key: `sciHeadquarterCity${index}`, type: 'text', hideIfEmpty: true },
          { key: `sciNafCode${index}`, type: 'text', hideIfEmpty: true },
        ],
      ),
      // Filiales
      { type: 'separator' as const, id: 'filiales', hideIfFieldEmpty: 'specificQuestionRCP2' },
      {
        key: 'specificQuestionRCP2',
        type: 'boolean',
        hideIfEmpty: true,
      },
      ...Array.from({ length: 5 }, (_, i) => i + 1).flatMap(
        (index): Array<InformationFieldParams | InformationSeparatorParams> => [
          ...(index > 1
            ? [
                {
                  type: 'separator' as const,
                  id: `filiale${index}`,
                  hideIfFieldEmpty: `filialeSiret${index}`,
                },
              ]
            : []),
          { key: `filialeSiret${index}`, type: 'siret', hideIfEmpty: true },
          { key: `filialeName${index}`, type: 'text', hideIfEmpty: true },
          { key: `filialeHeadquarterAddress${index}`, type: 'text', hideIfEmpty: true },
          { key: `filialeNafCode${index}`, type: 'text', hideIfEmpty: true },
        ],
      ),
      // Établissements secondaires
      {
        type: 'separator' as const,
        id: 'etablissementsSecondaires',
        hideIfFieldEmpty: 'specificQuestionRCP3',
      },
      {
        key: 'specificQuestionRCP3',
        type: 'boolean',
        hideIfEmpty: true,
      },
      ...Array.from({ length: 5 }, (_, i) => i + 1).flatMap(
        (index): Array<InformationFieldParams | InformationSeparatorParams> => [
          ...(index > 1
            ? [
                {
                  type: 'separator' as const,
                  id: `etablissement${index}`,
                  hideIfFieldEmpty: `etablissementSiret${index}`,
                },
              ]
            : []),
          { key: `etablissementSiret${index}`, type: 'siret', hideIfEmpty: true },
          { key: `etablissementName${index}`, type: 'text', hideIfEmpty: true },
          { key: `etablissementHeadquarterAddress${index}`, type: 'text', hideIfEmpty: true },
          { key: `etablissementNafCode${index}`, type: 'text', hideIfEmpty: true },
        ],
      ),
    ],
  },

  // ============================================================================
  // Quote & Premium Display
  // ============================================================================
  priceBreakdownConfig: {
    groups: [
      { lines: [{ key: 'INSURANCE_PREMIUM', variant: 'bold' }] },
      { lines: [{ key: 'WHOLESALE_ADDITIONAL_COMMISSION', variant: 'bold' }] },
      { lines: [{ key: 'KORINT_MANAGEMENT_FEE', variant: 'bold' }] },
      {
        variant: 'callout',
        lines: [
          { key: 'WHOLESALE_SUBSCRIPTION_FEE', variant: 'dimmed' },
          { key: 'RETAIL_SUBSCRIPTION_FEE', variant: 'dimmed' },
          { key: 'RETAIL_RENEWAL_FEE', variant: 'dimmed' },
        ],
      },
    ],
  },
  quoteTierModuleConfig: {
    goToTiersOnMta: '/mta/protection-juridique-pro/${policy.customer}/${policy.id}/new',
    goToTiersOnNewBusiness:
      '/discovery/protection-juridique-pro/${policy.customer}/${policy.id}/${workingBranch?.id}/quote',
    fieldsConfig: [],
    perilsMapping: {},
    isExternalQuoteEnabled: false,
    isChosenTierDisplayed: true,
  },
  delegatedQuoteModuleConfig: {
    showPerilOptions: false,
  },

  // ============================================================================
  // Discovery Funnel (New Business)
  // ============================================================================
  discoveryFunnelSteps: {
    afterEndPath: undefined,
    steps: [
      {
        path: 'asset',
        type: 'emptyAssetFunnel',
        hidden: true,
      },
      {
        path: 'company',
        type: 'companyFunnel',
        icon: Building2,
        additionalFields: companyCreationAdditionalFields,
        additionalSections: financialCompanySections,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'prior-history',
        type: 'policyFunnel',
        icon: AlertTriangle,
        sections: priorHistorySections,
      },
      {
        path: 'questions-filiales',
        type: 'policyFunnel',
        icon: Users,
        sections: questionsFiliales,
      },
      {
        path: 'questions-etablissements',
        type: 'policyFunnel',
        icon: Store,
        sections: questionsEtablissements,
      },
      {
        path: 'billing',
        type: 'billingSelectionFunnel',
        icon: CalendarClock,
        frequencyIcons: BILLING_FREQUENCY_ICONS,
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        icon: Shield,
        assetSections: [
          {
            title: 'Date de début de contrat',
            fields: [{ name: 'startedAt', min: 'startOfDay', icon: CalendarDays }],
          },
        ],
        manualQuoteSections,
        additionalQuoteComponentsSections,
        nonDelegatedDocuments: [
          'QUOTE_EP',
          'QUOTE_E',
          'QUOTE_O',
          'QUOTE_OP',
          'RELATION_CONTRACT_EP',
          'RELATION_CONTRACT_E',
          'RELATION_CONTRACT_O',
          'RELATION_CONTRACT_OP',
        ],
      },
      {
        path: 'invoicing-schedule',
        type: 'module',
        icon: CalendarDays,
        module: <InvoicesScheduleModule />,
      },
      {
        path: 'questions-sci',
        type: 'policyFunnel',
        icon: Scale,
        sections: questionsSCI,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: User,
        sections: customerFunnelSections,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'new-business-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: true,
          finishWithoutSendingButton: { enabled: true },
        },
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await policyControllerPushPolicyEvent(
              policyId,
              { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
              { branchId },
            );
          },
        },
      },
    ],
  },

  // ============================================================================
  // MTA Funnel (Mid-Term Adjustment)
  // ============================================================================
  mtaFunnelSteps: {
    steps: [
      {
        path: 'new',
        type: 'newMTACreation',
        icon: FilePen,
      },
      {
        path: 'company',
        type: 'companyFunnel',
        icon: Building2,
        additionalFields: companyCreationAdditionalFields,
        additionalSections: financialCompanySections,
      },
      {
        path: 'compliance',
        type: 'complianceFunnel',
        icon: BadgeCheck,
      },
      {
        path: 'prior-history',
        type: 'policyFunnel',
        icon: AlertTriangle,
        sections: priorHistorySections,
      },
      {
        path: 'questions-filiales',
        type: 'policyFunnel',
        icon: Users,
        sections: questionsFiliales,
      },
      {
        path: 'questions-etablissements',
        type: 'policyFunnel',
        icon: Store,
        sections: questionsEtablissements,
      },
      {
        path: 'quote',
        type: 'quoteFunnel',
        icon: Shield,
        assetSections: [
          {
            title: 'Date de début de contrat',
            fields: [{ name: 'startedAt', min: 'startOfDay', icon: CalendarDays }],
          },
        ],
        manualQuoteSections,
        additionalQuoteComponentsSections,
        nonDelegatedDocuments: [
          'QUOTE_EP',
          'QUOTE_E',
          'QUOTE_O',
          'QUOTE_OP',
          'RELATION_CONTRACT_EP',
          'RELATION_CONTRACT_E',
          'RELATION_CONTRACT_O',
          'RELATION_CONTRACT_OP',
        ],
      },
      {
        path: 'questions-sci',
        type: 'policyFunnel',
        icon: Scale,
        sections: questionsSCI,
      },
      {
        path: 'customer',
        type: 'customerFunnel',
        icon: User,
        sections: customerFunnelSections,
      },
      {
        path: 'end',
        type: 'funnelEnd',
        id: 'mta-end',
        icon: PartyPopper,
        additionalComponents: {
          isQuoteTierModuleVisible: false,
          finishWithoutSendingButton: { enabled: false },
        },
        callback: {
          callToActionCallback: async ({ policyId, branchId }) => {
            await policyControllerPushPolicyEvent(
              policyId,
              { eventType: 'TUTASSUR_NB_QUOTE_EMITTED' },
              { branchId },
            );
          },
        },
      },
    ],
  },

  // ============================================================================
  // Policy Dashboard Configuration
  // ============================================================================
  brokerPolicyDashboardHome: {
    isFinalizeNewBusinessModuleVisible: true,
    isAuthorCardModuleVisible: true,
    isPolicyholderInvitationModuleVisible: false,
    isQuoteTierModuleVisible: true,
    isPremiumModuleVisible: true,
    isPolicyLifecycleSummaryModuleVisible: true,
    isInvoicesBalanceModuleVisible: true,
  },
  policyDashboardEventsModuleConfig: {
    enableAssetComparison: false,
    quoteDerogationFunnelModule: {
      afterEndPath: '/derogations',
      steps: [
        {
          path: 'quote',
          type: 'quoteFunnel',
          manualQuoteSections,
          additionalQuoteComponentsSections,
          nonDelegatedDocuments: [
            'QUOTE_EP',
            'QUOTE_E',
            'QUOTE_O',
            'QUOTE_OP',
            'RELATION_CONTRACT_EP',
            'RELATION_CONTRACT_E',
            'RELATION_CONTRACT_O',
            'RELATION_CONTRACT_OP',
          ],
        },
      ],
    },
  },
  policyholderInvitationModuleConfig: {
    relationTypes: ['DRIVER'],
  },

  // ============================================================================
  // Finalize New Business
  // ============================================================================
  finalizeNewBusinessModuleConfig: {
    buttons: [
      {
        id: 'send-via-email',
        actionType: 'signature',
        visual: {
          icon: 'mail',
        },
        signature: {
          signatureType: 'direct',
          deliveryMode: 'EMAIL',
          otp: {
            enabled: false,
          },
        },
        visibilityConditions: {
          policyStatuses: ['POLICY_QUOTED'],
        },
      },
    ],
    rules: [
      {
        id: 'documents-import',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/documents`,
        isValid: ({ policy }) => policy.customFields.allClientDocumentsOk === true,
      },
      {
        id: 'sepa-payment',
        link: ({ policyId, branchId }) => `/policies/${policyId}/${branchId}/payment-methods`,
        isValid: ({ paymentMethods }) =>
          paymentMethods.some(({ paymentMethodType }) => paymentMethodType === 'sepa_debit'),
      },
    ],
  },

  // ============================================================================
  // Anonymous Funnels (Policyholder Self-Service)
  // ============================================================================
  customFunnels: [
    {
      ...anonymousDiscoveryBase,
      name: 'anonymous-discovery',
      basePath:
        '/anonymous/:brokerageFirmId/protection-juridique-pro/:customerId/:policyId/:branchId?',
      afterEndPath:
        '/anonymous/:brokerageFirmId/protection-juridique-pro-souscription/:customerId/:policyId/:branchId?/documents',
      layout: 'custom' as const,
      customLayout: tutassurCustomLayout,
    },
    {
      ...anonymousDiscoveryBase,
      name: 'anonymous-discovery-embedded',
      basePath:
        '/anonymous-embedded/:brokerageFirmId/protection-juridique-pro/:customerId/:policyId/:branchId?',
      afterEndPath:
        '/anonymous-embedded/:brokerageFirmId/protection-juridique-pro-souscription/:customerId/:policyId/:branchId?/documents',
      layout: 'embedded' as const,
    },
    {
      ...anonymousSubscriptionBase,
      name: 'anonymous-subscription',
      basePath:
        '/anonymous/:brokerageFirmId/protection-juridique-pro-souscription/:customerId/:policyId/:branchId?',
      layout: 'custom' as const,
      customLayout: tutassurCustomLayout,
    },
    {
      ...anonymousSubscriptionBase,
      name: 'anonymous-subscription-embedded',
      basePath:
        '/anonymous-embedded/:brokerageFirmId/protection-juridique-pro-souscription/:customerId/:policyId/:branchId?',
      layout: 'embedded' as const,
    },
  ],
} satisfies KorintModulesProductsConfig;
